import { useCallback } from "react";
import { pushGTMEvent } from "../utils/gtmHelpers";
import { useAgentAccountDetails } from "./common";

/**
 * Custom hook for pushing GTM events with automatic user context
 * @param {Object} props - Optional props object to be included in all events pushed by this hook instance
 * @returns {Function} pushEvent - Function to push GTM events with user context and props automatically included
 *
 * @example
 * // Without props
 * const pushEvent = useGtmPushEvent();
 * pushEvent({ event: "button_click", button_name: "Submit" });
 *
 * @example
 * // With props
 * const pushEvent = useGtmPushEvent({ page: "dashboard", section: "header" });
 * pushEvent({ event: "button_click", button_name: "Submit" });
 * // The event will include: { event: "button_click", button_name: "Submit", page: "dashboard", section: "header", userRole, userName, userHub, userId, timestamp }
 */
const useGtmPushEvent = (props = {}) => {
  const { agentRole, agentName, agentHub, agentAccountId } = useAgentAccountDetails();

  const pushEvent = useCallback(
    (eventData) => {
      if (!process.env.REACT_APP_GTM_ID) {
        return;
      }
      pushGTMEvent({
        ...props,
        ...eventData,
        userRole: agentRole,
        userName: agentName,
        userHub: agentHub?.hub_name,
        userId: agentAccountId,
        timestamp: eventData.timestamp || new Date().toISOString(),
      });
    },
    [agentRole, agentName, agentHub, agentAccountId, props]
  );

  return pushEvent;
};

export default useGtmPushEvent;
